import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { ChartProps, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Field, Form, Formik } from 'formik';
import { Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';

import { PageContainer } from 'components/UI';
import { OriginatorRatingChip } from 'components/UI/OriginatorRatingChip';
import Card from 'components/card';
import { FinancedPortfolioTable } from 'components/FinancedPortfolioTable';
import { Select } from 'components/forms';
import { SubmitFormOnChange } from 'components';
import { useFormatMoney } from 'hooks';
import { useCurrencySelection } from 'providers/currency-selection';
import { useLayoutPageTitle } from 'providers/layout-provider';
import { moveDecimalPoint, toPercent } from 'utils';
import { investorMarketOverviewDataSource, MarketOverviewDataSource } from './MarketOverviewPage/marketOverviewDataSource';

interface Values {
  country?: string;
  category?: 'CORPORATE' | 'PRIVATE' | 'REAL_ESTATE' | 'TBC';
  performance?: 'OnTime' | 'Overdue30' | 'Overdue60' | 'Overdue120' | 'TechnicalDefault' | 'Loss';
}

const colors = {
  backgroundColor: ['#254E8F', '#4E84C1', 'rgba(145, 154, 255, 0.95)', '#37A1E4', '#97D7FF', '#97FFF3'],
};

const fromCents = (value?: number) => moveDecimalPoint(value ?? 0, -2);
interface Props {
  dataSource?: MarketOverviewDataSource;
}

export const OriginatorDetailsPage = ({ dataSource = investorMarketOverviewDataSource }: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { selectedCurrency } = useCurrencySelection();
  const formatMoneyHook = useFormatMoney();
  const { providerId } = useParams<{ providerId: string }>();

  const formatMoneyFromCents = useCallback(
    (value?: number) => formatMoneyHook(fromCents(value)),
    [formatMoneyHook],
  );

  const { result: listedResult, loading: listedLoading } = useAsync(async () => {
    const { data } = await dataSource.getMarketOverviewListedOriginators();
    return data;
  }, [dataSource]);

  const providerRows = useMemo(
    () => (listedResult?.items ?? []).filter((item) => item.providerId === providerId),
    [listedResult, providerId],
  );

  const providerData = providerRows[0];
  const selectedProviderRow =
    providerRows.find((item) => item.currency === selectedCurrency) ?? providerData;

  const { result: marketResult, loading: marketLoading } = useAsync(async () => {
    if (!selectedCurrency) return null;

    const { data } = await dataSource.getMarketOverviewStatistics({
      currency: selectedCurrency,
      providerId,
    });

    return data;
  }, [selectedCurrency, providerId, dataSource]);

  const description = providerData?.description ?? '';
  const providerName = providerData?.originator ?? providerId;
  const providerLogoSrc = providerData?.logo ?? '';
  const marketOverviewUrl = url.replace(`/${providerId}`, '');
  const pageTitle = useMemo(
    () => (
      <span>
        {/*<Button sx={{ minWidth: '32px', padding: 0, marginRight: 1 }} onClick={() => history.push(marketOverviewUrl)}>*/}
        {/*  ←*/}
        {/*</Button>*/}
        {/*<br/>*/}
        <span style={{cursor: "pointer"}} onClick={() => history.push(marketOverviewUrl)}> {intl.formatMessage({ id: 'market_overview' })} ({selectedCurrency})</span>
        <br />
        {providerName}
      </span>
    ),
    [history, intl, marketOverviewUrl, providerName, selectedCurrency],
  );
  useLayoutPageTitle(pageTitle);
  const descriptionHtml = description.includes('<')
    ? description
    : description
        .split('\n')
        .map((line) => line.trim())
        .join('<br />');

  const [countryData, creditTypeData, performanceData] = useMemo(() => {
    const create = (name: string) => ({ labels: [] as string[], datasets: [{ ...colors, data: [] as number[], count: [] as string[], name }] });
    return [
      create(intl.formatMessage({ id: 'market_allocation_country' })),
      create(intl.formatMessage({ id: 'market_allocation_credit_type' })),
      create(intl.formatMessage({ id: 'market_allocation_performance' })),
    ];
  }, [intl]);

  const [allocationCountries, setAllocationCountries] = useState<string[]>([]);
  const [allocationCreditTypes, setAllocationCreditTypes] = useState<string[]>([]);

  const { execute: loadAllocation, loading: allocationLoading } = useAsyncCallback(async (values: Values) => {
    const { data } = await dataSource.getMarketAllocationStatistics({
      providerId: [providerId],
      currency: selectedCurrency,
      country: values.country ? [values.country] : undefined,
      productType: values.category ? [values.category] : undefined,
      overdue: values.performance ? [values.performance] : undefined,
    });

    countryData.labels = Object.keys(data.byCountry);
    countryData.datasets[0].data = Object.values(data.byCountry).map((v) => v.sum);
    countryData.datasets[0].count = Object.values(data.byCountry).map((v) => intl.formatMessage({ id: 'deals_count' }, { number: v.count }));

    creditTypeData.labels = Object.keys(data.byCreditType).map((key) => intl.formatMessage({ id: `borrower.${key}` }));
    creditTypeData.datasets[0].data = Object.values(data.byCreditType).map((v) => v.sum);
    creditTypeData.datasets[0].count = Object.values(data.byCreditType).map((v) => intl.formatMessage({ id: 'deals_count' }, { number: v.count }));

    performanceData.labels = Object.keys(data.byPerformance).map((value) => intl.formatMessage({ id: `originator.details.performance.${value}` }));
    performanceData.datasets[0].data = Object.values(data.byPerformance).map((v) => v.sum);
    performanceData.datasets[0].count = Object.values(data.byPerformance).map((v) => intl.formatMessage({ id: 'deals_count' }, { number: v.count }));

    if (!values.country && !values.category && !values.performance) {
      setAllocationCountries(Object.keys(data.byCountry).sort());
      setAllocationCreditTypes(Object.keys(data.byCreditType).sort());
    }
  });

  const pieChartProps = useMemo((): Partial<ChartProps<'pie'>> => ({
    width: 177,
    height: 177,
    style: { margin: 'auto' },
    options: {
      responsive: false,
      plugins: {
        datalabels: {
          display: true,
          color: '#fff',
          align: 'center',
          textAlign: 'center',
          formatter: (value, context) => {
            const label = (context.chart.data?.labels ?? [])[context.dataIndex] ?? '';
            const datapoints = context.chart.data.datasets[0].data as number[];
            const totalValue = datapoints.reduce((acc, val) => acc + (val ?? 0), 0);
            if (!totalValue) return '';
            const percentage = ((Number(value) / totalValue) * 100).toFixed(1);
            return [label, `${percentage}%`];
          },
        },
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (items) => {
              const item = items[0];
              const labelFromData = (item.chart.data.labels?.[item.dataIndex] as string | undefined) ?? '';
              const name = (item.dataset as any).name ?? '';
              return labelFromData ? labelFromData : name;
            },
            label: (item) => {
              const amount = formatMoneyHook(moveDecimalPoint(item.raw as number, -8), { prefix: `${selectedCurrency} `, suffix: ' m' });
              const count = (item.dataset as any).count?.[item.dataIndex];
              return [amount, count ? `${count}` : null].filter(Boolean) as string[];
            },
          },
        },
      },
    },
    plugins: [ChartDataLabels],
  }), [formatMoneyHook, selectedCurrency]);

  if (!listedLoading && !providerData) {
    throw new Error(`Originator ${providerId} was not found in listed originators.`);
  }

  const countriesForDisplay = allocationCountries.length ? allocationCountries : providerData?.countries ?? [];
  const creditTypesForFilters = allocationCreditTypes.length ? allocationCreditTypes : providerData?.creditTypes ?? [];

  return (
    <PageContainer maxWidth={false}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2} mt={4}>
        <Stack direction="row" spacing={2} alignItems="center" m={4}>
          <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(providerLogoSrc)}`} alt="logo" width={90} height={90}/>
          {/*<Avatar src={providerLogoSrc} alt={providerName} sx={{ bgcolor: '#153E75', fontWeight: 700, width: 44, height: 44 }}>*/}
          {/*{providerLogoLabel}*/}
          {/*</Avatar>*/}
          <Typography variant="h3" fontWeight={700}>{providerName}</Typography>
        </Stack>
        <OriginatorRatingChip rating={providerData?.originatorRating} showLabel />
      </Stack>

      <Grid container spacing={4} my={4}>
        <Grid item xs={12} lg={8}>
          <Card>
            {/*<Card.Header variant="condensed" title={<FormattedMessage id="originator.details.description" />} />*/}
            <Card.Body loadingContent={listedLoading}>
              <Typography component="div" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
            </Card.Body>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <Card.Header variant="condensed" title={<FormattedMessage id="originator.details.information" />} />
            <Card.Body loadingContent={listedLoading}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.rating" /></Typography>
                  <OriginatorRatingChip rating={providerData?.originatorRating} showLabel />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.countries" /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{countriesForDisplay.join(', ') || '-'}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.currencies" /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{providerRows.map((row) => row.currency).join(', ') || '-'}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography flexShrink={0}><FormattedMessage id="originator.details.credit_types" /></Typography>
                  <Typography fontWeight={700} flex={1} textAlign="right">{providerData?.creditTypes.map((value) => intl.formatMessage({ id: `borrower.${value}` })).join(', ') || '-'}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.historic_loan_volume_of_originator" values={{ providerName }} /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{formatMoneyFromCents(+(providerData?.loanVolumeBeforeLoantrade ?? 0) + (selectedProviderRow?.historicLoantradePortfolio ?? 0))}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.historic_loan_volume_on_loantrade" /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{formatMoneyFromCents(selectedProviderRow?.historicLoantradePortfolio ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.year_of_establishment" /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{providerData?.establishmentYear ?? '-'}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography><FormattedMessage id="originator.details.listing_on_loantrade_year" /></Typography>
                  <Typography fontWeight={700} sx={{textAlign: 'right'}}>{providerData?.listingOnLoantradeYear ?? '-'}</Typography>
                </Stack>
              </Stack>
            </Card.Body>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4} my={4}>
        <Grid item xs={12} lg={8}>
          <Card>
            <Card.Header variant="condensed" title={<Typography component="span" fontWeight={700}><FormattedMessage id="market_overview.financed_portfolio" /></Typography>} />
            <Card.Body loadingContent={marketLoading}>
              <FinancedPortfolioTable summary={marketResult?.summary} />
            </Card.Body>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={4}>
            <Card>
              <Card.Header
                variant="condensed"
                title={intl.formatMessage({ id: 'market_overview.current_portfolio.title' })}
              />
              <Card.Body loadingContent={marketLoading}>
                <Stack spacing={4}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="market_overview.current_portfolio.average_nominal_interest_rate" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(marketResult?.tiles.currentPortfolio.averageNominalInterestRate ?? 0)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="market_overview.current_portfolio.average_expected_default_rate" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(marketResult?.tiles.currentPortfolio.averageExpectedDefaultRate ?? 0)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="market_overview.current_portfolio.average_expected_net_return" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(marketResult?.tiles.currentPortfolio.averageExpectedNetReturn ?? 0)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="market_overview.listed_originators.column.par" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(selectedProviderRow?.par ?? 0)}</Typography></Stack>
                </Stack>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header
                variant="condensed"
                title={intl.formatMessage({ id: 'originator.details.historic_portfolio.title' })}
              />
              <Card.Body loadingContent={marketLoading}>
                <Stack spacing={4}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="originator.details.historic_portfolio.average_monthly_default_rate" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(marketResult?.tiles.historicPortfolio.averageMonthlyDefaultRate ?? 0)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography><FormattedMessage id="originator.details.historic_portfolio.average_monthly_recovery_rate" /></Typography>
                    <Typography fontWeight={700} sx={{textAlign: 'right'}}>{toPercent(marketResult?.tiles.historicPortfolio.averageMonthlyRecoveryRate ?? 0)}</Typography></Stack>
                </Stack>
              </Card.Body>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Grid item my={4}>
        <Formik<Values> key={selectedCurrency} initialValues={{}} onSubmit={loadAllocation}>
          <Form>
            <Card>
              <Card.Header variant="condensed" title={intl.formatMessage({ id: 'market_allocation' })} />
              <Card.Body>
                <Box display="flex" gap="16px" flexDirection={{ xs: 'column', sm: 'row' }}>
                  <Stack spacing={3}>
                    <Field id="country" name="country" sx={{ width: '180px' }} component={Select} options={[{ label: intl.formatMessage({ id: 'all' }), value: undefined }, ...countriesForDisplay.map((value) => ({ value, label: value }))]} label={intl.formatMessage({ id: 'filter_by_country' })} variant="filter" disableSelectedClass />
                    <Field id="category" name="category" sx={{ width: '180px' }} component={Select} options={[{ label: intl.formatMessage({ id: 'all' }), value: undefined }, ...creditTypesForFilters.map((value) => ({ value, label: intl.formatMessage({ id: `borrower.${value}` }) }))]} label={intl.formatMessage({ id: 'filter_by_product' })} variant="filter" disableSelectedClass />
                    <Field id="performance" name="performance" sx={{ width: '180px' }} component={Select} options={[{ label: intl.formatMessage({ id: 'all' }), value: undefined }, { value: 'OnTime', label: intl.formatMessage({ id: 'originator.details.performance.on_time' }) }, { value: 'Overdue30', label: intl.formatMessage({ id: 'originator.details.performance.1_30' }) }, { value: 'Overdue60', label: intl.formatMessage({ id: 'originator.details.performance.31_60' }) }, { value: 'Overdue120', label: intl.formatMessage({ id: 'originator.details.performance.61_120' }) }, { value: 'TechnicalDefault', label: intl.formatMessage({ id: 'originator.details.performance.default' }) }, { value: 'Loss', label: intl.formatMessage({ id: 'originator.details.performance.Loss' }) }]} label={intl.formatMessage({ id: 'originator.details.filter_by_performance' })} variant="filter" disableSelectedClass />
                  </Stack>
                  <Box display="flex" flexGrow={1} justifyContent="space-around" gap="16px" flexWrap="wrap">
                    {[{ title: intl.formatMessage({ id: 'country' }), data: countryData }, { title: intl.formatMessage({ id: 'borrower.type' }), data: creditTypeData }, { title: intl.formatMessage({ id: 'originator.details.performance' }), data: performanceData }].map((item) => (
                      <div key={item.title}>
                        <Typography variant="body1" color="#222" fontWeight={600} mb={1} textAlign={'center'}>{item.title}</Typography>
                        {allocationLoading ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={177}><CircularProgress /></Box> : <Pie {...pieChartProps} data={item.data as any} />}
                      </div>
                    ))}
                  </Box>
                </Box>
                <SubmitFormOnChange initialSubmit />
              </Card.Body>
            </Card>
          </Form>
        </Formik>
      </Grid>
    </PageContainer>
  );
};
